import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';
import { MedicalHistory } from '@models/medical-history.model';
import { User } from '@models/user.model';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@services/alert.service';
import { AuthService } from '@services/auth.service';
import { DisplayService } from '@services/display.service';
import { MedicalHistoryService } from '@services/medical-history.service';
import { DateTime } from 'luxon';
import { BehaviorSubject, EMPTY, from, Observable, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';

@Component({
  selector: 'gc-medical-history',
  templateUrl: './medical-history.component.html',
  styleUrls: ['./medical-history.component.scss'],
  standalone: false,
})
export class MedicalHistoryComponent implements OnInit, OnDestroy {
  @ViewChild('tabGroup', { static: false }) tabGroup: MatTabGroup;
  @ViewChild('saveToProfile') saveToProfile: MatCheckbox;
  @Input() medicalHistoryRequestForm: MedicalHistory;
  @Output() submittedMedicalHistory = new EventEmitter<Partial<MedicalHistory>>();
  tabLabels = [
    { section: 'general_health', label: 'generalHealth' },
    { section: 'family_history', label: 'familyHistory' },
    { section: 'allergies', label: 'allergies' },
    { section: 'chronic_items', label: 'chronicItems' },
    { section: 'operation_history', label: 'operationHistory' },
    { section: 'treatment_history', label: 'treatmentHistory' },
    { section: 'occupational_and_demographical_history', label: 'occupationalAndDemographicalHistory' },
    { section: 'vaccinations', label: 'vaccinations' },
    { section: 'travel_history', label: 'travelHistory' },
  ];
  user$: Observable<User>;
  public userId: number;
  unsubscribe$ = new Subject<void>();
  medicalHistory$: Observable<MedicalHistory>;
  isMobile$: BehaviorSubject<boolean>;
  tabLabel: string;
  currentTab: string;
  answeredSteps = [];
  selectedIndexBinding = 0;
  maxSteps = 9;
  updateProfile = false;
  constructor(
    protected readonly route: ActivatedRoute,
    protected readonly medicalHistoryService: MedicalHistoryService,
    protected readonly displayService: DisplayService,
    protected readonly alertService: AlertService,
    protected readonly auth: AuthService,
    protected readonly translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.user$ = this.route.data.pipe(
      filter((data) => !!data.user),
      map(({ user }) => user),
      tap((user) => (this.userId = user.id)),
    );

    this.isMobile$ = this.displayService.isMobile$;
    this.tabLabel = this.tabLabels.at(0).label;

    if (this.medicalHistoryRequestForm) {
      this.medicalHistoryService.$medicalHistory.set(this.medicalHistoryRequestForm);
      this.medicalHistory$ = new BehaviorSubject<MedicalHistory>(this.medicalHistoryRequestForm);
      this.medicalHistoryService.$medicalHistory.set(this.medicalHistoryRequestForm);
      return;
    }

    const formUuid = this.route.snapshot.paramMap.get('id');
    if (formUuid) {
      this.medicalHistoryService
        .getMedicalHistory$(formUuid)
        .pipe(
          take(1),
          tap((medicalHistory) => {
            this.medicalHistoryService.$medicalHistory.set(medicalHistory);
            this.medicalHistoryRequestForm = medicalHistory;
            this.medicalHistory$ = null;
          }),
          catchError((error: unknown) => {
            console.error(this.translateService.instant('error_getting_medical_history'), error);
            this.alertService.snackbar('error_getting_medical_history');
            return EMPTY;
          }),
        )
        .subscribe();
    } else {
      this.medicalHistory$ = this.user$.pipe(
        switchMap((user) => from(this.medicalHistoryService.getSavedMedicalHistory$(user.id))),
        catchError((error: unknown) => {
          console.error(this.translateService.instant('error_getting_medical_history'), error);
          return EMPTY;
        }),
      );
    }
  }

  get amountQuestions() {
    return this.answeredSteps.length;
  }

  tabLabelName(label: string) {
    if (!label) {
      this.tabLabel = this.tabLabels.at(0).label;
    }
    return label;
  }

  tabChange(event: MatTabChangeEvent) {
    const tabKey = this.tabLabels[event.index];
    this.currentTab = tabKey.label;
    this.tabLabel = tabKey.label;
    this.tabGroupIndexChange(event.index);
  }

  tabGroupIndexChange(index: number) {
    this.tabGroup.selectedIndex = index;
    this.selectedIndexBinding = index;
  }

  scrollTab(direction: number, tabKey: string) {
    const index = this.tabLabels.findIndex((tab) => tab.label === tabKey);
    let labelIndex: number;
    if (!this.tabLabel && index === 0) {
      labelIndex = -1;
    } else if (direction === -1 && index === this.tabLabels.length - 1) {
      labelIndex = -2;
    } else if (index === this.tabLabels.length - 1) {
      labelIndex = 0;
    } else {
      labelIndex = index + direction;
    }
    this.currentTab = this.tabLabels.at(labelIndex).label;
    this.tabLabel = this.currentTab;
    this.tabGroupIndexChange(labelIndex);
  }

  submittedStep(section: { step: string; values: any }) {
    this.medicalHistoryService.$medicalHistory.update((mh) => {
      mh[section.step] = section.values;
      return mh;
    });
    if (this.medicalHistoryRequestForm) {
      this.medicalHistoryRequestForm[section.step] = section.values;
    }
    if (this.answeredSteps.includes(section.step)) {
      return;
    }
    this.answeredSteps.push(section.step);
    if (section.step !== this.tabLabels.at(-1).label) {
      this.scrollTab(1, section.step);
    }
  }

  getDataFromProfile() {
    if (!this.auth.isAuthenticated()) {
      this.alertService
        .loginDialog$()
        .pipe(
          take(1),
          switchMap((result) => {
            if (result) {
              return this.medicalHistoryService.getSavedMedicalHistory$(this.auth.getUserId());
            }
            return EMPTY;
          }),
          tap((profileForm) => {
            this.medicalHistoryRequestForm = this.medicalHistoryService.setMedicalHistoryStages(
              this.medicalHistoryRequestForm,
              profileForm,
            );
            this.medicalHistoryService.$medicalHistory.set(this.medicalHistoryRequestForm);
            this.saveToRequest();
            this.alertService.snackbar(`medical_history_saved`);
          }),
        )
        .subscribe();
    } else {
      this.medicalHistoryService
        .getSavedMedicalHistory$(this.auth.getUserId())
        .pipe(
          take(1),
          tap((profileForm) => {
            this.alertService.snackbar(`medical_history_saved`);
            this.medicalHistoryRequestForm = this.medicalHistoryService.setMedicalHistoryStages(
              this.medicalHistoryRequestForm,
              profileForm,
            );
            this.medicalHistoryService.$medicalHistory.set(this.medicalHistoryRequestForm);
            this.saveToRequest();
          }),
        )
        .subscribe();
    }
  }

  saveToRequest() {
    const answeredStepsTemp = [];
    for (const section of this.tabLabels) {
      this.medicalHistoryService
        .saveRequestMedicalHistory$(
          this.medicalHistoryRequestForm.uuid,
          section.section,
          this.medicalHistoryService.$medicalHistory()[section.label],
        )
        .pipe(take(1))
        .subscribe(() => {
          answeredStepsTemp.push(section.label);
        });
    }
    this.answeredSteps = this.tabLabels.map((tap) => tap.label);
  }

  submitMedicalHistory() {
    if (this.saveToProfile.checked) {
      const newMedicalHistory = {
        general_health: this.medicalHistoryService.$medicalHistory().generalHealth,
        family_history: this.medicalHistoryService.$medicalHistory().familyHistory,
        allergies: this.medicalHistoryService.$medicalHistory().allergies,
        chronic_items: this.medicalHistoryService.$medicalHistory().chronicItems,
        operation_history: this.medicalHistoryService.$medicalHistory().operationHistory,
        treatment_history: this.medicalHistoryService.$medicalHistory().treatmentHistory,
        occupational_and_demographical_history: this.medicalHistoryService.$medicalHistory().occupationalAndDemographicalHistory,
        vaccinations: this.medicalHistoryService.$medicalHistory().vaccinations,
        travel_history: this.medicalHistoryService.$medicalHistory().travelHistory,
        gynaecological_history: this.medicalHistoryService.$medicalHistory().gynaecologicalHistory,
      };
      if (!this.auth.isAuthenticated()) {
        this.alertService
          .loginDialog$()
          .pipe(
            take(1),
            switchMap((result) => {
              if (result) {
                return this.medicalHistoryService
                  .saveMedicalHistory$(this.auth.getUserId(), null, { user_id: this.userId, medical_history: newMedicalHistory })
                  .pipe(take(1));
              }
              return EMPTY;
            }),
          )
          .subscribe(() => {
            this.medicalHistoryRequestForm.respondedOn = DateTime.now();
            this.medicalHistoryRequestForm.accepted = true;
          });
      } else {
        this.medicalHistoryService
          .saveMedicalHistory$(this.auth.getUserId(), null, { user_id: this.userId, medical_history: newMedicalHistory })
          .pipe(take(1))
          .subscribe();
      }
    }
    this.alertService
      .confirmDialog$(
        this.answeredSteps.length === this.maxSteps
          ? this.translateService.instant('submit_request')
          : this.translateService.instant('only_answeredSteps_answered', { count: this.answeredSteps.length }),
        {
          detail:
            this.translateService.instant('confirm_submit_request_detail') +
            '<br/>' +
            (this.answeredSteps.length < this.maxSteps
              ? this.translateService.instant('only_answered_of_total_answered_detail', {
                  answered: this.answeredSteps.length,
                  total: this.maxSteps,
                })
              : ''),
        },
      )
      .pipe(
        take(1),
        filter((result) => !!result),
        switchMap(() =>
          this.medicalHistoryService.submitRequestMedicalHistory$(
            this.medicalHistoryRequestForm.uuid,
            this.medicalHistoryService.$medicalHistory(),
          ),
        ),
      )
      .subscribe(() => {
        this.medicalHistoryRequestForm.respondedOn = DateTime.now();
        this.medicalHistoryRequestForm.accepted = true;
        this.alertService.snackbar('medical_history_successfully_submitted');
        this.submittedMedicalHistory.emit(this.medicalHistoryService.$medicalHistory());
      });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
